/**
 * BonsaiMate — AI Assistant Panel for CaseMinder
 * Phase 1: Conversational agent with tool calling, proactive suggestions, action approval
 *
 * Contexts ("context" prop):
 *   "dashboard" (default) — Dashboard slide-over. Cross-matter awareness, full feature set.
 *   "matter"              — Mounted on matter detail pages. Right-rail embedded (collapsible)
 *                           on ≥1280px, slide-over fallback below. Picker locked to the page's
 *                           matter; today-queue + full history view suppressed; chat/execute
 *                           payloads carry matter_reference + surface: "matter_detail";
 *                           suggestions are matter-scoped server-side.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  Sparkles, Send, AlertTriangle, Clock, CheckCircle, ChevronRight,
  Loader2, X, Trash2, RefreshCw, ArrowRight, PoundSterling, Calendar,
  FileText, Bot, User, Zap, ExternalLink, PenTool, Copy, Search,
  Mail, FileEdit, MessageSquare, StickyNote, ChevronLeft, Paperclip,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { bonsaiMateSessionId, bonsaiMateMatterSessionId } from "./AIAssistant";
import MateQuoteInsightsNudge from "./MateQuoteInsightsNudge";  // BM-H80

const MATE_API = API;

// Brief 51.1 (14 Jul 2026) — Help→Mate merge. QUESTION_CATEGORIES
// ported verbatim from the retired ``HelpSidebar.jsx`` so newer staff
// still get the discoverability the standalone Help sidebar gave
// them. Rendered as a starter-chip row on the empty Mate panel; hides
// once the user sends their first message.
export const MATE_QUESTION_CATEGORIES = [
  {
    label: "Dashboard & Matters",
    questions: [
      "How do I open a new matter?",
      "How do I see the progress of matters being opened?",
      "What do the CCL status labels mean?",
      "How do I review and approve a Client Care Letter?",
      "What does the Client Onboarding tab show?",
      "What do the email delivery statuses mean (Success, Failed, Historical)?",
    ],
  },
  {
    label: "Completions Book & Checklists",
    questions: [
      "What is the Completions Book?",
      "How do I create a new Completions Book entry?",
      "Why is the completion date locked?",
      "What email reminders does the Completions Book send?",
      "What is the Pre-Exchange Checklist?",
      "What is the Pre-Completion Checklist?",
      "What happens if the completion date changes after exchange?",
    ],
  },
  {
    label: "Client Onboarding & Compliance",
    questions: [
      "How do I resend a client onboarding email?",
      "Why does the onboarding tracker show 'Historical' for some records?",
      "How are duplicate onboarding entries handled?",
      "Can a matter proceed with a deferred conflict check?",
      "How does the director hard-delete work?",
    ],
  },
  {
    label: "Invoices & Workflows",
    questions: [
      "How do I request an invoice?",
      "How do I close a file?",
      "What is the Completion Statement workflow?",
      "What is the Conditions Precedent (CP) Deal Checklist?",
    ],
  },
  {
    label: "Bonsai Mail (Outlook)",
    questions: [
      "How do I file an email to SharePoint using AI suggestions?",
      "How do I file directly to a favourite folder?",
      "How do I search for past emails and documents?",
      "Does the AI see client email content?",
    ],
  },
  {
    label: "Policies & Compliance",
    questions: [
      "Where should I file client emails in SharePoint?",
      "How quickly must I file emails?",
      "What are my responsibilities as a fee earner?",
      "How do we comply with UK GDPR and SRA requirements?",
    ],
  },
  {
    label: "Troubleshooting",
    questions: [
      "What should I do if CaseMinder won't load?",
      "I can't sign in to CaseMinder or Bonsai Mail — what next?",
      "AI search isn't finding anything — how can I fix it?",
      "Who do I contact for training or support?",
    ],
  },
];

// Quick-action chips on the empty dashboard-mode Mate panel. Exported so the
// wording is a testable contract rather than a literal buried in the render.
//
// BM-H114.1 (27 Jul 2026) — "Inbox" leads: the inbox list is the day-one
// prompt for a fee earner, and it was only reachable by typing it out. The
// prompt text is the exact phrase already proven to hit the right tool path,
// so it is deliberately not paraphrased.
export const MATE_DASHBOARD_QUICK_PROMPTS = [
  {
    label: "Inbox",
    sublabel: "What's currently in my inbox folder",
    prompt: "Give me an updated list of what is currently in my inbox folder",
  },
  { label: "What needs doing today?", prompt: "What should I focus on today? Check my deadlines, stale matters, and any urgent items." },
  { label: "My overdue deadlines", prompt: "Show me all my overdue deadlines." },
  { label: "Stale matters", prompt: "Which of my matters have gone quiet? Show dormant files." },
  { label: "Credit control status", prompt: "Give me the credit control overview — who owes money?" },
];

// Brief 50.2 (14 Jul 2026) — payment-request nudge storage key.
// Comma-separated array of payment-request ids the user has already
// been shown the nudge for. Reset whenever the user opens the
// dashboard widget or the Tracker page (both surfaces stamp this key).
export const MATE_PAYMENT_REQUESTS_SEEN_KEY = "paymentRequestsSeen";

function _seenIds() {
  try {
    const raw = localStorage.getItem(MATE_PAYMENT_REQUESTS_SEEN_KEY) || "";
    return new Set(raw.split(",").map(s => s.trim()).filter(Boolean));
  } catch { return new Set(); }
}


const userId = () => sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
const headers = () => ({ "X-Bonsai-User-Id": userId() });

const NARROW_VIEWPORT_BREAKPOINT = 1280;  // px — below this, "matter" context falls back to slide-over

export const collapsePersistenceKey = (uid) => `mate_panel_collapsed_${uid || "anon"}`;

// BM-H538 (15 Sep 2026) — the dashboard's own client-cached session id
// (BM-H275's dashboardSessionPersistenceKey / readStoredDashboardSession /
// writeStoredDashboardSession) is gone. Identity now does the routing:
// the server resolves "this person's latest ambient session" itself
// (mate.py's _bmh538_resolve_ambient_session_id), the same shared session
// Outlook and Teams continue too, so the dashboard no longer needs — or is
// allowed — to pin its own id in localStorage. A refresh or reopen still
// resumes the same thread because the server remembers it per-person, not
// because the browser does.


const SEVERITY_STYLES = {
  high: { bg: "bg-status-red-dim/40", border: "border-red-800/50", icon: AlertTriangle, iconColor: "text-status-red", badge: "bg-status-red-dim text-status-red" },
  medium: { bg: "bg-status-amber-dim/30", border: "border-amber-800/40", icon: Clock, iconColor: "text-status-amber", badge: "bg-status-amber-dim text-status-amber" },
  low: { bg: "bg-teal-dim/30", border: "border-teal/40", icon: CheckCircle, iconColor: "text-teal", badge: "bg-teal-dim text-teal" },
};

function SuggestionCard({ suggestion }) {
  const style = SEVERITY_STYLES[suggestion.severity] || SEVERITY_STYLES.medium;
  const Icon = style.icon;
  return (
    <div className={`${style.bg} border ${style.border} rounded-md px-3 py-2.5 flex items-start gap-2.5`} data-testid={`suggestion-${suggestion.type}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white leading-tight">{suggestion.title}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{suggestion.detail}</p>
      </div>
      <span className={`text-[9px] px-1.5 py-0.5 rounded ${style.badge} shrink-0`}>{suggestion.severity}</span>
    </div>
  );
}

function ActionCard({ action, onApprove, onDismiss, executing }) {
  return (
    <div className="bg-navy border border-teal/30 rounded-md px-3 py-2.5" data-testid={`action-${action.action}`}>
      <div className="flex items-start gap-2">
        <Zap className="w-3.5 h-3.5 text-teal mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-teal">{action.action.replace(/_/g, " ")}</p>
          <p className="text-[10px] text-slate-300 mt-0.5">{action.description}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onDismiss} className="text-[10px] h-6 px-2 text-slate-500 hover:text-slate-300" data-testid="dismiss-action-btn">
          Dismiss
        </Button>
        <Button size="sm" onClick={onApprove} disabled={executing} className="text-[10px] h-6 px-3 bg-teal text-txt-faint hover:bg-teal-hover font-semibold" data-testid="approve-action-btn">
          {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Approve"}
        </Button>
      </div>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && <Bot className="w-4 h-4 text-teal mt-1 shrink-0" />}
      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${
        isUser
          ? "bg-teal/15 text-white border border-teal/20"
          : "bg-navy text-slate-200 border border-navy-border"
      }`}>
        <p className="whitespace-pre-wrap">{msg.text}</p>
      </div>
      {isUser && <User className="w-4 h-4 text-slate-400 mt-1 shrink-0" />}
    </div>
  );
}

const DRAFT_TYPES = [
  { key: "reply_to_email", label: "Reply to email", icon: Mail, placeholder: "Paste the email you're replying to..." },
  { key: "chase_email", label: "Chase email to client", icon: Send, placeholder: "What are you chasing? e.g. signed contract, ID docs..." },
  { key: "letter_to_other_side", label: "Letter to other side", icon: FileEdit, placeholder: "Describe the issue and desired outcome..." },
  { key: "matter_note", label: "Matter note / attendance", icon: StickyNote, placeholder: "What happened? e.g. call with client re completion date..." },
];

function DraftPanel({ lockedMatter = null }) {
  const [draftType, setDraftType] = useState(null);
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(lockedMatter);
  const [context, setContext] = useState("");
  const [extraInstructions, setExtraInstructions] = useState("");
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [searchingMatter, setSearchingMatter] = useState(false);
  const isPickerLocked = !!lockedMatter;

  useEffect(() => {
    // Keep DraftPanel's matter in sync if parent's lockedMatter changes
    if (lockedMatter) {
      setSelectedMatter(lockedMatter);
      setMatterResults([]);
      setMatterSearch("");
    }
  }, [lockedMatter]);

  const searchMatters = async () => {
    if (!matterSearch.trim()) return;
    setSearchingMatter(true);
    try {
      // BM-H3270: this box used to run a full Mate agent turn (Sonnet 5 /
      // Opus 5) whose answer was thrown away, then read a submissions path
      // the backend does not serve, so it never found anything. It now uses the
      // shared client/matter search, whose model calls run on Haiku.
      const searchRes = await axios.get(`${API}/smart-search`, {
        params: { q: matterSearch, limit: 5, matters_only: true },
        headers: headers(),
      });
      const items = searchRes.data?.results || [];
      setMatterResults(items.slice(0, 5));
    } catch {
      // Fallback: just set the search term as the matter ref
      setMatterResults([]);
    }
    setSearchingMatter(false);
  };

  const generateDraft = async () => {
    if (!draftType) return;
    setGenerating(true);
    setDraft(null);
    try {
      const res = await axios.post(`${MATE_API}/mate/draft`, {
        draft_type: draftType,
        matter_ref: selectedMatter?.matter_reference || matterSearch || null,
        context: context || null,
        additional_instructions: extraInstructions || null,
      }, { headers: headers() });
      setDraft(res.data);
    } catch {
      setDraft({ draft_text: "Failed to generate draft. Please try again.", error: true });
    }
    setGenerating(false);
  };

  const copyToClipboard = () => {
    if (draft?.draft_text) {
      navigator.clipboard.writeText(draft.draft_text);
      toast.success("Copied to clipboard");
    }
  };

  if (!draftType) {
    return (
      <div className="px-3 py-4">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">What would you like to draft?</p>
        <div className="space-y-1.5">
          {DRAFT_TYPES.map((dt) => {
            const Icon = dt.icon;
            return (
              <button
                key={dt.key}
                onClick={() => setDraftType(dt.key)}
                className="w-full text-left px-3 py-2.5 rounded-md border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2.5"
                data-testid={`draft-type-${dt.key}`}
              >
                <Icon className="w-4 h-4 text-teal shrink-0" />
                <span className="text-xs text-slate-300">{dt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const typeConfig = DRAFT_TYPES.find(d => d.key === draftType);

  return (
    <div className="px-3 py-3 space-y-3">
      {/* Back + type label */}
      <div className="flex items-center gap-2">
        <button onClick={() => { setDraftType(null); setDraft(null); setContext(""); setSelectedMatter(null); }} className="text-[10px] text-slate-500 hover:text-white">← Back</button>
        <span className="text-[10px] text-teal font-semibold">{typeConfig?.label}</span>
      </div>

      {/* Matter search — hidden when picker is locked to a parent-supplied matter (matter-detail context) */}
      {!isPickerLocked && (
      <div>
        <label className="text-[10px] text-slate-500 block mb-1">Matter (optional)</label>
        <div className="flex gap-1.5">
          <input
            type="text"
            value={selectedMatter ? `${selectedMatter.matter_reference} — ${selectedMatter.client_name || selectedMatter.data?.Q1 || ""}` : matterSearch}
            onChange={(e) => { setMatterSearch(e.target.value); setSelectedMatter(null); }}
            placeholder="Search by client or ref..."
            className="flex-1 bg-navy-card border border-navy-border rounded-md px-2.5 py-1.5 text-[11px] text-white placeholder:text-slate-600 focus:outline-none focus:border-teal/50"
            onKeyDown={(e) => e.key === "Enter" && searchMatters()}
            data-testid="draft-matter-search"
          />
          <Button size="sm" onClick={searchMatters} disabled={searchingMatter} className="h-7 w-7 p-0 bg-navy-hover hover:bg-navy-hover">
            {searchingMatter ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          </Button>
        </div>
        {matterResults.length > 0 && !selectedMatter && (
          <div className="mt-1 border border-navy-border rounded-md bg-navy max-h-32 overflow-y-auto">
            {matterResults.map((m, i) => (
              <button
                key={m.id || i}
                onClick={() => { setSelectedMatter(m); setMatterResults([]); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-navy-hover text-[10px] text-slate-300 border-b border-navy-border/30 last:border-0"
              >
                <span className="font-mono text-teal">{m.matter_reference}</span> — {m.client_name || m.data?.Q1 || ""}
              </button>
            ))}
          </div>
        )}
      </div>
      )}
      {isPickerLocked && (
        <div data-testid="draft-matter-locked" className="text-[10px] text-slate-500">
          Matter: <span className="font-mono text-teal">{selectedMatter?.matter_reference}</span>
        </div>
      )}

      {/* Context textarea */}
      <div>
        <label className="text-[10px] text-slate-500 block mb-1">Context</label>
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder={typeConfig?.placeholder}
          rows={4}
          className="w-full bg-navy-card border border-navy-border rounded-md px-2.5 py-2 text-[11px] text-white placeholder:text-slate-600 focus:outline-none focus:border-teal/50 resize-none"
          data-testid="draft-context"
        />
      </div>

      {/* Extra instructions */}
      <div>
        <label className="text-[10px] text-slate-500 block mb-1">Additional instructions (optional)</label>
        <input
          type="text"
          value={extraInstructions}
          onChange={(e) => setExtraInstructions(e.target.value)}
          placeholder="e.g. Keep it brief, mention the deposit..."
          className="w-full bg-navy-card border border-navy-border rounded-md px-2.5 py-1.5 text-[11px] text-white placeholder:text-slate-600 focus:outline-none focus:border-teal/50"
          data-testid="draft-instructions"
        />
      </div>

      {/* Generate button */}
      <Button
        onClick={generateDraft}
        disabled={generating || !context.trim()}
        className="w-full bg-teal text-txt-faint hover:bg-teal-hover font-semibold text-xs h-8"
        data-testid="generate-draft-btn"
      >
        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PenTool className="w-3.5 h-3.5 mr-1.5" />}
        {generating ? "Generating..." : "Generate Draft"}
      </Button>

      {/* Draft output */}
      {draft && (
        <div className="space-y-2">
          {draft.subject_line && (
            <div className="bg-navy border border-navy-border rounded-md px-3 py-2">
              <p className="text-[10px] text-slate-500">Subject</p>
              <p className="text-xs text-white font-medium">{draft.subject_line}</p>
            </div>
          )}
          <div className={`bg-navy border rounded-md px-3 py-2.5 ${draft.error ? "border-red-800/50" : "border-navy-border"}`}>
            <p className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed" data-testid="draft-output">{draft.draft_text}</p>
          </div>
          {!draft.error && (
            <div className="flex gap-2">
              <Button size="sm" onClick={copyToClipboard} className="text-[10px] h-6 px-3 bg-navy-hover hover:bg-navy-hover text-white" data-testid="copy-draft-btn">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.location.href = "/bonsai"} className="text-[10px] h-6 px-3 text-teal hover:text-white">
                <Mail className="w-3 h-3 mr-1" /> Open in Bonsai Mail
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BonsaiMate({ open, onClose, context = "dashboard", matterReference = null, panelUserId = null }) {
  const isMatterMode = context === "matter";
  const effectiveUserId = panelUserId || userId() || "anon";
  const isOpen = isMatterMode ? true : !!open;  // matter mode is always "open" — collapse is the equivalent

  const [activeTab, setActiveTab] = useState("chat"); // "chat" or "draft"
  const [suggestions, setSuggestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // BM-H538 — dashboard mode no longer seeds a client-cached session id;
  // the server resolves the person's shared ambient session on the first
  // request (loadHistory / sendMessage below), same as Outlook and Teams.
  const [sessionId, setSessionId] = useState(() => (
    isMatterMode && matterReference
      ? bonsaiMateMatterSessionId(effectiveUserId, matterReference)
      : null
  ));
  const [executingAction, setExecutingAction] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined" || !isMatterMode) return false;
    try {
      return window.localStorage.getItem(collapsePersistenceKey(effectiveUserId)) === "true";
    } catch {
      return false;
    }
  });
  const [isNarrow, setIsNarrow] = useState(() => (
    typeof window !== "undefined" && window.innerWidth < NARROW_VIEWPORT_BREAKPOINT
  ));
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  // Web-pane paperclip: files staged for the next send. Each entry is
  // { filename, content_type, content_b64, size }. Cleared after send.
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const MAX_ATTACHMENTS = 5;
  const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

  // Track viewport so matter mode falls back to slide-over below 1280px
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsNarrow(window.innerWidth < NARROW_VIEWPORT_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persist collapse state per user (matter mode only)
  useEffect(() => {
    if (!isMatterMode || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(collapsePersistenceKey(effectiveUserId), String(collapsed));
    } catch {
      /* ignore quota / blocked storage */
    }
  }, [collapsed, isMatterMode, effectiveUserId]);

  // When the page's matter changes (user navigates to a different matter), reset the session
  useEffect(() => {
    if (!isMatterMode) return;
    const newSid = matterReference
      ? bonsaiMateMatterSessionId(effectiveUserId, matterReference)
      : null;
    setSessionId(newSid);
    setMessages([]);
    setActions([]);
    setSuggestions([]);
  }, [isMatterMode, matterReference, effectiveUserId]);

  // Load suggestions on open (or on mount when in matter mode)
  useEffect(() => {
    if (isOpen && !collapsed) {
      fetchSuggestions();
      loadHistory();
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, collapsed, matterReference]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const params = isMatterMode && matterReference ? { matter_reference: matterReference } : {};
      const res = await axios.get(`${MATE_API}/mate/suggestions`, { headers: headers(), params });
      setSuggestions(res.data.suggestions || []);
    } catch {
      setSuggestions([]);
    }
    setLoadingSuggestions(false);
  };

  const loadHistory = async () => {
    try {
      // In matter mode, scope history fetch to the matter session id so we resume
      // the prior conversation about THIS matter, not the user's global last chat.
      // BM-H538 — on the dashboard, sessionId is intentionally null here; the
      // server's own "latest session for this user" fallback in /mate/history
      // resolves the person's shared ambient session, so a refresh reloads the
      // same thread without the client having cached anything itself.
      const params = sessionId ? { session_id: sessionId } : {};
      const res = await axios.get(`${MATE_API}/mate/history`, { headers: headers(), params });
      if (res.data.messages?.length) {
        setMessages(res.data.messages);
        if (res.data.session_id) {
          setSessionId(res.data.session_id);
        }
      }
    } catch {}
  };

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    const staged = attachments;
    // Allow a file-only send (paperclip, no typed text).
    if (!trimmed && staged.length === 0) return;
    const attachLabel = staged.length
      ? (trimmed ? trimmed + "\n" : "") + staged.map(a => `📎 ${a.filename}`).join("\n")
      : trimmed;
    const userMsg = { role: "user", text: attachLabel };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setAttachments([]);
    setSending(true);
    setActions([]);

    try {
      const payload = {
        message: trimmed,
        context: "caseminder",
      };
      if (staged.length) {
        payload.attachments = staged.map(a => ({
          filename: a.filename,
          content_type: a.content_type,
          content_b64: a.content_b64,
        }));
      }
      if (isMatterMode) {
        // Matter mode is the one surface that still trusts a client-supplied
        // session id — it is deliberately scoped to this matter, not the
        // person's ambient identity, and is explicitly out of scope for
        // BM-H538. Unchanged: session_id + matter_reference + surface.
        payload.session_id = sessionId;
        payload.matter_reference = matterReference || null;
        payload.surface = "matter_detail";
      } else {
        // BM-H538 — ambient dashboard chat sends no session_id at all. The
        // server resolves (and, for a brand-new user, creates) this
        // person's shared ambient session from X-Bonsai-User-Id, the same
        // resolver Outlook and Teams now use, so "ask me anywhere" is one
        // continuous memory instead of three separately-cached ids.
        payload.surface = "dashboard";
      }
      const res = await axios.post(`${MATE_API}/mate/chat`, payload, { headers: headers() });

      setSessionId(res.data.session_id);
      // BM-H140 (Audit 06 P0-2, 2026-09-01) — belt-and-braces empty guard.
      // Backend post-strip invariant already promises a non-empty message,
      // but historic and off-version deploys can still return "" (e.g. the
      // scrub-only strip path pre-fix). Pushing an empty string here was
      // the exact blank assistant bubble Vanessa saw on 01 Sep. Never push
      // an empty message; substitute a neutral acknowledgement.
      const rawMessage = res.data.message;
      const messageText = (rawMessage && String(rawMessage).trim())
        ? rawMessage
        : (res.data.actions?.length
            ? "Ready when you are — see the card above."
            : "I finished that step but did not produce a reply. Ask me what you'd like next.");
      setMessages(prev => [...prev, { role: "assistant", text: messageText }]);
      if (res.data.actions?.length) {
        setActions(res.data.actions);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]);
    }
    setSending(false);
  }, [sessionId, isMatterMode, matterReference, attachments]);

  // ── Web-pane paperclip: stage files for the next send ──
  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result || "";
      const comma = String(res).indexOf(",");
      resolve(comma >= 0 ? String(res).slice(comma + 1) : String(res));
    };
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });

  const handleFilesPicked = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAttachments(prev => {
      const room = MAX_ATTACHMENTS - prev.length;
      if (room <= 0) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      }
      return prev;
    });
    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`“${file.name}” is larger than 10 MB.`);
        continue;
      }
      let staged;
      setAttachments(prev => { staged = prev; return prev; });
      if (staged && staged.length >= MAX_ATTACHMENTS) break;
      try {
        const b64 = await readFileAsBase64(file);
        setAttachments(prev => (
          prev.length >= MAX_ATTACHMENTS
            ? prev
            : [...prev, {
                filename: file.name,
                content_type: file.type || "",
                content_b64: b64,
                size: file.size,
              }]
        ));
      } catch {
        toast.error(`Could not read “${file.name}”.`);
      }
    }
  }, []);

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleApproveAction = async (action) => {
    setExecutingAction(action.action);
    try {
      const payload = {
        action: action.action,
        params: action.params,
      };
      if (isMatterMode) {
        // Matter mode: unchanged, explicit session_id + matter scoping.
        payload.session_id = sessionId;
        payload.matter_reference = matterReference || null;
        payload.surface = "matter_detail";
      } else {
        // BM-H538 — ambient dashboard execute also drops the client session
        // id; the server resolves the person's shared ambient session, same
        // as /mate/chat above.
        payload.surface = "dashboard";
      }
      const res = await axios.post(`${MATE_API}/mate/execute`, payload, { headers: headers() });
      // BM-H162 (02 Sep 2026) — the toast type MUST match the actual outcome.
      // Previously every /mate/execute response landed as toast.success even
      // when the server returned {success: false, message: "bulk_delete_emails
      // failed: Delete failed: 500"}. That painted a green tick over a red
      // error and gave the LLM enough of a mixed signal to narrate the failure
      // as "the server is having a backend issue" on the next turn. Read
      // res.data.success and show the actual message.
      // BM-H269 (03 Sep 2026) - `dispatched` is the authoritative signal. The
      // server now answers one deterministic question, did the action
      // actually happen, and returns it as `dispatched`. A delete that came
      // back {success: true, deleted_count: 0} used to satisfy the check
      // below and paint a green tick over an inbox that had not changed, so
      // a false `dispatched` is a failure regardless of what `success` says.
      const dispatchedOk = !(res.data && res.data.dispatched === false);
      const succeeded = res.data && res.data.success !== false && dispatchedOk;
      const msg = (res.data && res.data.message) || (succeeded ? "Action completed" : "Action failed");
      if (succeeded) {
        toast.success(msg);
      } else {
        toast.error(msg, { autoClose: 8000 });
      }
      // Only drop the card on success — a failed action stays available so
      // the user can retry or edit.
      if (succeeded) {
        setActions(prev => prev.filter(a => a !== action));
      }
      if (res.data && res.data.redirect) {
        window.open(res.data.redirect, "_blank");
      }
    } catch (err) {
      // BM-H162 — include the network failure code so the user knows this is
      // network-side, not action-side. Never say "backend issue" — that
      // wording is what triggered the reported bug.
      const status = (err && err.response && err.response.status) || "network";
      toast.error(`Could not reach the action service (${status}). Please retry.`, { autoClose: 8000 });
    }
    setExecutingAction(null);
  };

  const handleClearHistory = async () => {
    try {
      if (isMatterMode && sessionId) {
        // Matter mode: scoped clear — delete only this matter's session
        await axios.delete(`${MATE_API}/mate/history`, {
          headers: headers(),
          params: { session_id: sessionId },
        });
      } else {
        await axios.delete(`${MATE_API}/mate/history`, { headers: headers() });
      }
      setMessages([]);
      setActions([]);
      if (!isMatterMode) {
        // Clearing the chat is the one place a new session is what the user
        // asked for. BM-H538: there is no client-cached id to forget anymore
        // — the next ambient turn simply gets a fresh session created
        // server-side once this cleared session stops being "the latest".
        setSessionId(null);
      }
      toast.success("Chat cleared");
    } catch {}
  };

  const quickPrompts = isMatterMode ? [
    { label: "What's the status of this matter?", prompt: `Give me a status summary for matter ${matterReference}.` },
    { label: "What's outstanding?", prompt: "What's outstanding on this matter — pending forms, missing IDs, overdue tasks?" },
    { label: "Draft a chase email", prompt: "Draft a chase email to the client for the outstanding items on this matter." },
    { label: "Show me the FORF gaps", prompt: "Walk me through any incomplete FORF sections on this matter." },
  ] : MATE_DASHBOARD_QUICK_PROMPTS;

  if (!isOpen) return null;

  // Matter mode + wide viewport → embedded right-rail with collapse;
  // dashboard mode OR narrow viewport → slide-over.
  const isEmbedded = isMatterMode && !isNarrow;

  // Collapsed strip: in matter mode (embedded OR narrow) we keep a
  // slim re-open affordance, because matter mode has no dashboard-level
  // "closed" state — the panel must be reachable again.
  if (isMatterMode && collapsed) {
    const hasUnread = suggestions.length > 0 || actions.length > 0;
    // A short tab at the top right, not a full-height strip: the page's own
    // scroll bar runs down that edge and a full-height rail covered it
    // (Vanessa, 28 Oct 2026: "the matter page seems to have lost [its
    // scroll bar]").
    return (
      <aside
        className="fixed top-12 right-0 w-10 bg-navy-card border border-r-0 border-navy-border rounded-l-md z-30 flex flex-col items-center py-3"
        data-testid="bonsai-mate-panel-collapsed"
        aria-label="Bonsai Mate (collapsed)"
      >
        <button
          onClick={() => setCollapsed(false)}
          title="Open Bonsai Mate"
          className="relative w-7 h-7 rounded-full bg-teal/20 hover:bg-teal/30 flex items-center justify-center transition-colors"
          data-testid="mate-expand-btn"
        >
          <Sparkles className="w-4 h-4 text-teal" />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-status-amber" data-testid="mate-unread-badge" />
          )}
        </button>
        <button
          onClick={() => setCollapsed(false)}
          className="mt-2 text-slate-500 hover:text-teal"
          title="Open"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      </aside>
    );
  }

  const containerClass = isEmbedded
    ? "fixed inset-y-0 right-0 w-[360px] bg-navy-card border-l border-navy-border z-30 flex flex-col shadow-2xl"
    : "fixed inset-y-0 right-0 w-full sm:w-[420px] bg-navy-card border-l border-navy-border z-50 flex flex-col shadow-2xl";

  return (
    <div
      className={containerClass}
      data-testid={isMatterMode ? "bonsai-mate-panel-matter" : "bonsai-mate-panel"}
      data-context={context}
      data-matter-reference={matterReference || ""}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border bg-navy">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-teal/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-teal" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">Bonsai Mate</h2>
            <p className="text-[9px] text-txt-muted">
              {isMatterMode && matterReference ? (
                <>scoped to <span className="font-mono text-teal">{matterReference}</span></>
              ) : "AI Assistant"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleClearHistory} className="h-7 w-7 p-0 text-slate-500 hover:text-slate-300" title="Clear chat" data-testid="clear-mate-history">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchSuggestions} className="h-7 w-7 p-0 text-slate-500 hover:text-slate-300" title="Refresh suggestions" data-testid="refresh-suggestions">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {isEmbedded ? (
            <Button variant="ghost" size="sm" onClick={() => setCollapsed(true)} className="h-7 w-7 p-0 text-slate-500 hover:text-white" title="Collapse" data-testid="mate-collapse-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            // BM-H10: on matter mode + narrow viewport we still need the X to
            // do SOMETHING even when the parent didn't pass onClose (which is
            // the normal case for MatterDetail-mounted instances — matter mode
            // has no dashboard-level closed state). Fall back to collapsing to
            // the sidebar strip. On dashboard mode onClose is always wired.
            <Button variant="ghost" size="sm" onClick={onClose || (() => setCollapsed(true))} className="h-7 w-7 p-0 text-slate-500 hover:text-white" title={onClose ? "Close" : "Collapse"} data-testid="close-mate-panel">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-navy-border">
        <button
          onClick={() => setActiveTab("chat")}
          className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5 ${
            activeTab === "chat" ? "text-teal border-b-2 border-teal" : "text-slate-500 hover:text-slate-300"
          }`}
          data-testid="mate-tab-chat"
        >
          <MessageSquare className="w-3 h-3" /> Chat
        </button>
        <button
          onClick={() => setActiveTab("draft")}
          className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5 ${
            activeTab === "draft" ? "text-teal border-b-2 border-teal" : "text-slate-500 hover:text-slate-300"
          }`}
          data-testid="mate-tab-draft"
        >
          <PenTool className="w-3 h-3" /> Draft
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Draft tab */}
        {activeTab === "draft" && (
          <DraftPanel
            lockedMatter={isMatterMode && matterReference ? { matter_reference: matterReference } : null}
          />
        )}

        {/* Chat tab content */}
        {activeTab === "chat" && suggestions.length > 0 && (
          <div className="px-3 py-3 border-b border-navy-border/50">
            <div className="flex items-center gap-1.5 mb-2">
              <Zap className="w-3 h-3 text-teal" />
              <span className="text-[10px] font-semibold text-teal uppercase tracking-wider">Attention needed</span>
            </div>
            <div className="space-y-1.5">
              {suggestions.map((s, i) => <SuggestionCard key={i} suggestion={s} />)}
            </div>
          </div>
        )}
        {activeTab === "chat" && loadingSuggestions && suggestions.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-teal" />
          </div>
        )}

        {/* Brief 51.1 (14 Jul 2026) — Help→Mate merge starter chips.
            Show ONLY on empty Mate; hide once the user has sent their
            first message. Also drops the Brief 50.2 payment-request
            nudge line at the top of the same panel. */}
        {activeTab === "chat" && messages.length === 0 && (
          <MatePaymentRequestsNudge setInput={setInput} />
        )}
        {/* BM-H80 warm-quote nudge — sits with the payment nudge on empty Mate. */}
        {activeTab === "chat" && messages.length === 0 && (
          <MateQuoteInsightsNudge />
        )}
        {activeTab === "chat" && messages.length === 0 && (
          <MateStarterChips onPromptClick={(q) => setInput(q)} />
        )}

        {/* Quick prompts (show when no messages) */}
        {activeTab === "chat" && messages.length === 0 && (
          <div className="px-3 py-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Quick actions</p>
            <div className="grid grid-cols-2 gap-1.5">
              {quickPrompts.map((qp, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(qp.prompt)}
                  className="text-left px-2.5 py-2 rounded-md border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors text-[10px] text-slate-300 leading-tight"
                  data-testid={`quick-prompt-${i}`}
                >
                  {qp.label}
                  {qp.sublabel && (
                    <span className="block mt-0.5 text-[9px] text-slate-500 font-normal">
                      {qp.sublabel}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages */}
        {activeTab === "chat" && messages.length > 0 && (
          <div className="px-3 py-3 space-y-3">
            {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
            {sending && (
              <div className="flex gap-2 items-center">
                <Bot className="w-4 h-4 text-teal shrink-0" />
                <div className="bg-navy border border-navy-border rounded-lg px-3 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-teal" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}

        {/* Action cards */}
        {activeTab === "chat" && actions.length > 0 && (
          <div className="px-3 pb-3 space-y-1.5">
            <p className="text-[10px] text-teal uppercase tracking-wider font-semibold">Proposed actions — approve to execute</p>
            {actions.map((a, i) => (
              <ActionCard
                key={i}
                action={a}
                onApprove={() => handleApproveAction(a)}
                onDismiss={() => setActions(prev => prev.filter((_, j) => j !== i))}
                executing={executingAction === a.action}
              />
            ))}
          </div>
        )}
      </div>

      {/* Input — chat tab only */}
      {activeTab === "chat" && (
        <div className="border-t border-navy-border px-3 py-2.5 bg-navy">
          {/* Staged attachments (web-pane paperclip) */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="mate-attachment-list">
              {attachments.map((att, i) => (
                <span
                  key={`${att.filename}-${i}`}
                  className="inline-flex items-center gap-1 max-w-[200px] bg-navy-card border border-navy-border rounded-md pl-2 pr-1 py-1 text-[11px] text-slate-200"
                  data-testid="mate-attachment-chip"
                  title={att.filename}
                >
                  <Paperclip className="w-3 h-3 text-teal shrink-0" />
                  <span className="truncate">{att.filename}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="shrink-0 text-slate-500 hover:text-white"
                    aria-label={`Remove ${att.filename}`}
                    data-testid="mate-attachment-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
            className="flex items-center gap-2"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }}
              data-testid="mate-file-input"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={sending || attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
              className="text-slate-400 hover:text-teal hover:bg-navy-card h-8 w-8 p-0"
              title="Attach a file"
              aria-label="Attach a file"
              data-testid="mate-attach-btn"
            >
              <Paperclip className="w-3.5 h-3.5" />
            </Button>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Mate anything..."
              className="flex-1 bg-navy-card border border-navy-border rounded-md px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-teal/50"
              disabled={sending}
              data-testid="mate-chat-input"
            />
            <Button
              type="submit"
              size="sm"
              disabled={sending || (!input.trim() && attachments.length === 0)}
              className="bg-teal text-txt-faint hover:bg-teal-hover h-8 w-8 p-0"
              data-testid="mate-send-btn"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Brief 51.1 (14 Jul 2026) — Help→Mate merge: starter-chip row.
// Brief 50.2 (14 Jul 2026) — payment-request nudge.
// Both mounted on the empty Mate panel, hidden once the user sends
// their first message. Extracted as named exports so the Jest suite
// can drive them without spinning up the whole Mate panel.
// ═══════════════════════════════════════════════════════════════════════════

export function MateStarterChips({ onPromptClick }) {
  const [openCategory, setOpenCategory] = useState(null);
  return (
    <div className="px-3 pt-3 pb-2" data-testid="mate-starter-chips">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
        Learn how to…
      </p>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {MATE_QUESTION_CATEGORIES.map((cat, i) => (
          <button
            key={cat.label}
            type="button"
            onClick={() => setOpenCategory(openCategory === i ? null : i)}
            data-testid={`mate-starter-chip-${i}`}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors
              ${openCategory === i
                ? "bg-teal/15 border-teal/40 text-teal"
                : "bg-navy border-navy-border/60 text-slate-300 hover:border-teal/40"}`}
          >
            {cat.label}
          </button>
        ))}
      </div>
      {openCategory !== null && (
        <div
          className="mt-2 space-y-1"
          data-testid={`mate-starter-chip-questions-${openCategory}`}
        >
          {MATE_QUESTION_CATEGORIES[openCategory].questions.map((q, qi) => (
            <button
              key={qi}
              type="button"
              onClick={() => onPromptClick(q)}
              data-testid={`mate-starter-question-${openCategory}-${qi}`}
              className="w-full text-left text-[11px] text-slate-300 px-2.5 py-1.5 rounded
                bg-navy/60 border border-navy-border/40 hover:border-teal/40 hover:text-teal
                transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MatePaymentRequestsNudge({ setInput }) {
  const [unseenCount, setUnseenCount] = useState(0);
  const [feInitials, setFeInitials] = useState("");
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hub/finance/payment-requests`, {
        params: { mine: true, outstanding_only: true, limit: 50 },
        headers: headers(),
      });
      const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const seen = _seenIds();
      const unseen = rows.filter((r) => !seen.has(r.id));
      setUnseenCount(unseen.length);
      setFeInitials(res.data?.filters?.fee_earner || "");
    } catch {
      // Guardrail: silent no-op on error — no toast, no console spam.
      setUnseenCount(0);
    }
  }, []);

  useEffect(() => {
    load();
    // Every 60s so an incoming payment request shows up without a hard
    // refresh. Same cadence as PaymentRequestsWidget.
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  if (dismissed || unseenCount === 0) return null;

  const href = feInitials
    ? `/finance/payment-tracker?fee_earner=${encodeURIComponent(feInitials)}&outstanding=1`
    : `/finance/payment-tracker?outstanding=1`;

  return (
    <div
      className="mx-3 mt-2 mb-1 px-3 py-2 rounded-md border border-teal/30 bg-teal/5 flex items-center gap-2"
      data-testid="mate-payment-requests-nudge"
    >
      <PoundSterling className="w-3.5 h-3.5 text-teal shrink-0" />
      <a
        href={href}
        onClick={() => setDismissed(true)}
        className="flex-1 text-[11px] text-teal hover:underline truncate"
        data-testid="mate-payment-requests-nudge-link"
      >
        {unseenCount} new payment request{unseenCount === 1 ? "" : "s"} awaiting you
      </a>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-teal/60 hover:text-white"
        aria-label="Dismiss"
        data-testid="mate-payment-requests-nudge-dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
